import { useEffect, useState, useCallback, useRef } from "react";
import { Link, useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { Plus, MoreHorizontal, Pencil, PowerOff, Repeat } from "lucide-react";

import { api, formatApiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import PageHeader from "@/components/PageHeader";
import { useWithBack } from "@/lib/nav";
import StatusBadge from "@/components/StatusBadge";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Switch } from "@/components/ui/switch";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import {
  DropdownMenu, DropdownMenuTrigger, DropdownMenuContent, DropdownMenuItem, DropdownMenuSeparator,
} from "@/components/ui/dropdown-menu";
import { formatDateOnly } from "@/lib/format";

const FREQ_LABELS = {
  monthly: "Monthly",
  quarterly: "Quarterly",
  six_monthly: "6-Monthly",
  annual: "Annual",
  custom_days: "Custom (days)",
};

const FREQ_OPTIONS = [
  { value: "all", label: "All frequencies" },
  { value: "monthly", label: "Monthly" },
  { value: "quarterly", label: "Quarterly" },
  { value: "six_monthly", label: "6-Monthly" },
  { value: "annual", label: "Annual" },
  { value: "custom_days", label: "Custom" },
];

export default function ServiceSchedulesPage() {
  const withBackHref = useWithBack();
  const navigate = useNavigate();
  const { hasPermission } = useAuth();
  const canWrite = hasPermission("schedule.write");

  const [items, setItems] = useState([]);
  const [machines, setMachines] = useState({});
  const [sites, setSites] = useState({});
  const [customers, setCustomers] = useState({});
  const [freqFilter, setFreqFilter] = useState("all");
  const [showInactive, setShowInactive] = useState(false);
  const [loading, setLoading] = useState(true);
  const reqIdRef = useRef(0);

  const load = useCallback(async () => {
    const myReqId = ++reqIdRef.current;
    setLoading(true);
    try {
      const params = { limit: 200 };
      if (freqFilter !== "all") params.frequency = freqFilter;
      if (!showInactive) params.active = "true";
      const { data } = await api.get("/service-schedules", { params });
      if (myReqId !== reqIdRef.current) return;
      const rows = data.items || [];
      setItems(rows);

      // Hydrate machine / site / customer names
      const machineIds = Array.from(new Set(rows.map((r) => r.machine_id).filter(Boolean)));
      if (machineIds.length === 0) {
        setMachines({}); setSites({}); setCustomers({});
        return;
      }
      const mResults = await Promise.all(machineIds.map((id) => api.get(`/machines/${id}`).then((r) => r.data).catch(() => null)));
      const mMap = {}, sMap = {}, cMap = {};
      for (const m of mResults) {
        if (!m) continue;
        mMap[m.id] = m;
        if (m.site) sMap[m.site.id] = m.site;
        if (m.customer) cMap[m.customer.id] = m.customer;
      }
      if (myReqId !== reqIdRef.current) return;
      setMachines(mMap); setSites(sMap); setCustomers(cMap);
    } catch (err) {
      if (myReqId !== reqIdRef.current) return;
      toast.error(formatApiError(err));
    } finally {
      if (myReqId === reqIdRef.current) setLoading(false);
    }
  }, [freqFilter, showInactive]);

  useEffect(() => { load(); }, [load]);

  const handleDeactivate = async (row) => {
    try {
      await api.delete(`/service-schedules/${row.id}`);
      toast.success("Schedule deactivated");
      load();
    } catch (err) { toast.error(formatApiError(err)); }
  };

  const toggleActive = async (row, active) => {
    try {
      await api.patch(`/service-schedules/${row.id}`, { active });
      toast.success(active ? "Schedule activated" : "Schedule paused");
      load();
    } catch (err) { toast.error(formatApiError(err)); }
  };

  return (
    <div className="p-6 space-y-6" data-testid="schedules-page">
      <PageHeader
        testId="schedules-header"
        title="Service Schedules"
        subtitle="Recurring service intervals per machine — controls the Due Soon queue."
        actions={canWrite && (
          <Button asChild size="sm" data-testid="schedule-new-button">
            <Link to="/scheduling/schedules/new"><Plus className="h-4 w-4" /> New Schedule</Link>
          </Button>
        )}
      />

      <div className="flex items-center justify-between gap-3 flex-wrap">
        <div className="flex items-center gap-3 flex-wrap">
          <Select value={freqFilter} onValueChange={setFreqFilter}>
            <SelectTrigger className="h-8 w-52 text-xs" data-testid="schedules-freq-filter">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              {FREQ_OPTIONS.map((f) => (<SelectItem key={f.value} value={f.value}>{f.label}</SelectItem>))}
            </SelectContent>
          </Select>
        </div>
        <div className="flex items-center gap-2">
          <Label className="text-xs text-muted-foreground uppercase tracking-wider font-bold">Show inactive</Label>
          <Switch checked={showInactive} onCheckedChange={setShowInactive} data-testid="schedules-show-inactive" />
        </div>
      </div>

      <Card className="rounded-md overflow-hidden">
        <CardContent className="p-0">
          <table className="w-full text-sm" data-testid="schedules-table">
            <thead className="bg-muted/50 border-b border-border">
              <tr className="text-xs font-bold uppercase tracking-wider text-muted-foreground">
                <th className="text-left py-2 px-3">Machine</th>
                <th className="text-left py-2 px-3">Customer</th>
                <th className="text-left py-2 px-3">Site</th>
                <th className="text-left py-2 px-3">Frequency</th>
                <th className="text-left py-2 px-3">Anchor</th>
                <th className="text-left py-2 px-3">Next due</th>
                <th className="text-left py-2 px-3">Active</th>
                <th className="text-right py-2 px-3">Actions</th>
              </tr>
            </thead>
            <tbody>
              {loading ? (
                <tr><td colSpan={8} className="py-8 text-center text-xs text-muted-foreground">Loading…</td></tr>
              ) : items.length === 0 ? (
                <tr><td colSpan={8} className="py-8 text-center text-xs text-muted-foreground" data-testid="schedules-empty">
                  No service schedules yet.
                </td></tr>
              ) : items.map((row) => {
                const m = machines[row.machine_id];
                const s = m?.site ? sites[m.site.id] : null;
                const c = m?.customer ? customers[m.customer.id] : null;
                return (
                  <tr key={row.id} data-testid={`schedule-row-${row.id}`} className="border-b border-border last:border-0 hover:bg-muted/30">
                    <td className="py-2 px-3">
                      {m ? (
                        <Link to={withBackHref(`/machines/${m.id}`)} className="font-medium hover:text-primary" data-testid={`schedule-machine-link-${row.id}`}>
                          {m.make} {m.model}
                        </Link>
                      ) : <span className="text-muted-foreground">—</span>}
                      {m?.serial && <div className="text-xs text-muted-foreground font-mono">Serial {m.serial}</div>}
                    </td>
                    <td className="py-2 px-3 text-muted-foreground">{c?.name || "—"}</td>
                    <td className="py-2 px-3 text-muted-foreground">{s?.name || "—"}</td>
                    <td className="py-2 px-3">
                      <StatusBadge tone="info">
                        {row.frequency === "custom_days" ? `Every ${row.custom_days}d` : (FREQ_LABELS[row.frequency] || row.frequency)}
                      </StatusBadge>
                    </td>
                    <td className="py-2 px-3 text-muted-foreground whitespace-nowrap">{formatDateOnly(row.anchor_due_date)}</td>
                    <td className="py-2 px-3 whitespace-nowrap">{m?.next_due_at ? formatDateOnly(m.next_due_at) : <span className="text-muted-foreground">—</span>}</td>
                    <td className="py-2 px-3">
                      <Switch
                        checked={!!row.active}
                        onCheckedChange={(v) => toggleActive(row, v)}
                        disabled={!canWrite}
                        data-testid={`schedule-active-${row.id}`}
                      />
                    </td>
                    <td className="py-2 px-3 text-right">
                      <DropdownMenu>
                        <DropdownMenuTrigger asChild>
                          <Button variant="ghost" size="icon" className="h-7 w-7" data-testid={`schedule-actions-${row.id}`}>
                            <MoreHorizontal className="h-4 w-4" />
                          </Button>
                        </DropdownMenuTrigger>
                        <DropdownMenuContent align="end">
                          {canWrite && (
                            <DropdownMenuItem onClick={() => navigate(`/scheduling/schedules/${row.id}/edit`)} data-testid={`schedule-edit-${row.id}`}>
                              <Pencil className="h-4 w-4 mr-2" /> Edit
                            </DropdownMenuItem>
                          )}
                          {m && (
                            <DropdownMenuItem onClick={() => navigate(`/machines/${m.id}`)} data-testid={`schedule-view-machine-${row.id}`}>
                              <Repeat className="h-4 w-4 mr-2" /> View machine
                            </DropdownMenuItem>
                          )}
                          {canWrite && row.active && (<>
                            <DropdownMenuSeparator />
                            <DropdownMenuItem className="text-destructive focus:text-destructive" onClick={() => handleDeactivate(row)} data-testid={`schedule-deactivate-${row.id}`}>
                              <PowerOff className="h-4 w-4 mr-2" /> Deactivate
                            </DropdownMenuItem>
                          </>)}
                        </DropdownMenuContent>
                      </DropdownMenu>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </CardContent>
      </Card>
    </div>
  );
}
